import React from 'react'
import styled from 'styled-components'
import { Wrapper, Grid, H, Image, P } from '@farewill/ui'
import {
  GTR,
  CONTENT_OUTER_WIDTH,
  CONTAINER_PADDING_VERTICAL_AT_BP,
  COLOR,
  FONT,
} from '@farewill/ui/tokens'
import { screenMin } from '@farewill/ui/helpers/responsive'
import { CLOUDINARY_ROOT_PATH } from '@farewill/ui/components/Image/constants'
import PreventOrphan from 'components/PreventOrphan'

type FormatPathArgs = { ext: string; path: string; width: number }

const IMAGE_MAX_WIDTH_BREAKPOINT = 1695
const DEFAULT_IMAGE_TRANSFORMATION_PARAMS = {
  small: 'ar_1.8,c_fill,f_auto,q_auto,g_faces,w_640',
  medium: 'ar_1:2,c_fill,f_auto,q_auto',
  large: 'ar_0.75,c_fill,f_auto,q_auto,g_faces',
  extraLarge: 'ar_1.4,c_crop,f_auto,q_auto,g_faces,z_0.9/h_650',
}

const StyledRelativeWrapper = styled(Wrapper)`
  position: relative;
  overflow: hidden;
  ${screenMin.xl`
    max-width: ${CONTENT_OUTER_WIDTH};
    margin: 0 auto;
  `}
`

const StyledGrid = styled(Grid)`
  gap: 0;
  align-items: center;

  ${screenMin.xl`
    max-width: ${CONTENT_OUTER_WIDTH};
    margin: 0 auto;
  `}

  @media (min-width: ${IMAGE_MAX_WIDTH_BREAKPOINT + parseInt(GTR.XXL, 10)}px) {
    max-width: calc(
      (100vw - ${CONTENT_OUTER_WIDTH}) / 2 + ${CONTENT_OUTER_WIDTH}
    );
    margin-right: 0;
  }
`

const StyledImageGridItem = styled(Grid.Item)`
  align-self: stretch;

  ${screenMin.xl`   
    && {
      margin: -${GTR[CONTAINER_PADDING_VERTICAL_AT_BP.L]} 0;
    }
  `}

  @media (min-width: ${IMAGE_MAX_WIDTH_BREAKPOINT}px) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;

    && {
      margin: 0;
    }
  }
`

const StyledSmallImage = styled(Image)`
  clip-path: circle(170% at 50% -148%);

  ${screenMin.m`
    display: none;
  `}
`

const StyledMediumImage = styled(Image)`
  display: none;
  clip-path: circle(75% at 120% 50%);

  ${screenMin.m`
    display: block;
  `}

  ${screenMin.l`
    display: none;
  `}
`

const StyledLargeImage = styled(Image)`
  display: none;
  clip-path: circle(105% at 125% 50%);
  max-width: 100%;

  ${screenMin.l`
    display: block;
  `}

  ${screenMin.xl`
    display: none;
  `}
`

const StyledExtraLargeImage = styled(Image)`
  display: none;
  clip-path: circle(105% at 100% 50%);
  height: 100%;

  ${screenMin.xl`
    display: block;
  `}
`

const StyledContentGridItem = styled(Grid.Item)`
  padding: 24px;
  ${screenMin.m`
    padding: 40px 24px 40px 40px;
  `}
  ${screenMin.xl`
    padding: 60px 24px 60px 60px;
  `}
`

const StyledTestimonialHeading = styled(H)`
  font-size: 20px;
  ${screenMin.m`
    font-weight: ${FONT.WEIGHT.MEDIUM};
    font-size: ${FONT.SIZE.XL}
  `}
`

const StyledTestimonialText = styled(P)`
  font-size: ${FONT.SIZE.M};
  ${screenMin.m`
    font-size: 22px;
  `}

  ${screenMin.l`
    margin: 24px 0;
    font-size: ${FONT.SIZE.L};
  `}
`

const StyledTestimonialSignOff = styled(P)`
  font-weight: ${FONT.WEIGHT.BOLD};
  ${screenMin.m`
    font-size: ${FONT.SIZE.L};
    font-weight: ${FONT.WEIGHT.MEDIUM};
  `}
`

const Testimonial = (): React.ReactElement => {
  const testimonialImagePath = `com-assets/bespoke-journey-cruk/testimonial`

  return (
    <StyledRelativeWrapper background="#FFEC95">
      <StyledGrid
        gap={0}
        gapFromL="XL"
        gapFromXL="XXL"
        $minHeight={400}
        columns={12}
      >
        <StyledImageGridItem
          span={12}
          spanFromM={5}
          startColumnFromM={8}
          spanFromL={6}
          startColumnFromL={7}
          spanFromXL={7}
          startColumnFromXL={6}
        >
          <StyledSmallImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${DEFAULT_IMAGE_TRANSFORMATION_PARAMS.small}/${path}.${ext}`
            }
            path={testimonialImagePath}
            ext="jpg"
            stretch
          />

          <StyledMediumImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${DEFAULT_IMAGE_TRANSFORMATION_PARAMS.medium}/${path}.${ext}`
            }
            path={testimonialImagePath}
            ext="jpg"
            stretch
          />

          <StyledLargeImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${DEFAULT_IMAGE_TRANSFORMATION_PARAMS.large}/${path}.${ext}`
            }
            path={testimonialImagePath}
            ext="jpg"
          />

          <StyledExtraLargeImage
            formatPath={({ ext, path }: FormatPathArgs) =>
              `${CLOUDINARY_ROOT_PATH}/${DEFAULT_IMAGE_TRANSFORMATION_PARAMS.extraLarge}/${path}.${ext}`
            }
            path={testimonialImagePath}
            ext="jpg"
          />
        </StyledImageGridItem>

        <StyledContentGridItem
          span={12}
          spanFromM={7}
          spanFromL={6}
          spanFromXL={5}
          container
        >
          <StyledTestimonialHeading color={COLOR.BLACK}>
            <PreventOrphan>
              Why Doreen is leaving a gift to Cancer Research UK
            </PreventOrphan>
          </StyledTestimonialHeading>

          <StyledTestimonialText color={COLOR.BLACK}>
            &ldquo;When I was diagnosed with leukaemia in 1998, my life
            expectancy was 3-5 years. After undergoing treatment until 2024, my
            last tests showed cancer was undetectable. I am so glad to have
            achieved what I have in the last 27 years. In 2015, I arranged to
            leave a percentage share gift in my Will to Cancer Research UK, and
            the process was very simple.&rdquo;
          </StyledTestimonialText>

          <StyledTestimonialSignOff color={COLOR.BLACK}>
            Doreen, pledger, Greater London
          </StyledTestimonialSignOff>
        </StyledContentGridItem>
      </StyledGrid>
    </StyledRelativeWrapper>
  )
}

export default Testimonial
